/**
 * Expense API client. Wraps /v1/expense/* + /v1/me/expense-claims (ADR-0020).
 * The two-stage approval surface (manager → finance), receipts, the finance
 * portfolio list, and the off-cycle mark-paid settlement.
 */
import { apiRequest, apiDownload } from './client.js';

export type ExpenseClaimStatus =
  | 'draft'
  | 'pending_manager'
  | 'pending_finance'
  | 'approved'
  | 'reimbursed'
  | 'paid_offcycle'
  | 'rejected'
  | 'cancelled';

export type ExpenseCategory =
  | 'travel'
  | 'meals'
  | 'accommodation'
  | 'supplies'
  | 'communications'
  | 'other';

export const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  'travel',
  'meals',
  'accommodation',
  'supplies',
  'communications',
  'other',
];

/** Accepted receipt types (mirrors EXPENSE_RECEIPT_ALLOWED_TYPES + the CV upload). */
export const RECEIPT_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
export const RECEIPT_ALLOWED_LABEL = 'PDF, Word (.doc/.docx), or image (JPG/PNG)';

export interface ExpenseClaim {
  id: string;
  employeeId: string;
  /** Denormalised on approvals + portfolio + detail so the queue can show who it's for. */
  employee?: { firstName: string; lastName: string; employeeNumber: string } | null;
  amount: string;
  currency: string;
  category: ExpenseCategory;
  description: string;
  incurredDate: string;
  status: ExpenseClaimStatus;
  managerApproverUserId: string | null;
  financeApproverUserId: string | null;
  decisionReason: string | null;
  reimbursedByRunId: string | null;
  reimbursedPayComponentId: string | null;
  offcyclePaidMethod: string | null;
  offcyclePaidReference: string | null;
  createdAt: string;
}

export interface ExpenseReceipt {
  id: string;
  claimId: string;
  kind: string;
  label: string | null;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedById: string;
  uploadedAt: string;
}

// ---- claimant self-service --------------------------------------------------

export function listMyClaims(): Promise<{ claims: ExpenseClaim[] }> {
  return apiRequest(`/v1/me/expense-claims`);
}

export function createClaim(body: {
  amount: string;
  currency: string;
  category: ExpenseCategory;
  description: string;
  incurredDate: string;
}): Promise<ExpenseClaim> {
  return apiRequest(`/v1/expense/claims`, { method: 'POST', body });
}

export function submitClaim(id: string): Promise<ExpenseClaim> {
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(id)}/submit`, { method: 'POST', body: {} });
}

export function cancelClaim(id: string): Promise<ExpenseClaim> {
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: {} });
}

// ---- approvals (dual-mount: manager + finance stages) -----------------------

export function listApprovals(view: 'pending_my_approval' | 'all'): Promise<{ claims: ExpenseClaim[] }> {
  return apiRequest(`/v1/expense/approvals?view=${view}`);
}

export function managerApproveClaim(id: string): Promise<unknown> {
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(id)}/manager-approve`, { method: 'POST', body: {} });
}
export function managerRejectClaim(id: string, body: { reason: string }): Promise<unknown> {
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(id)}/manager-reject`, { method: 'POST', body });
}
export function financeApproveClaim(id: string): Promise<unknown> {
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(id)}/finance-approve`, { method: 'POST', body: {} });
}
export function financeRejectClaim(id: string, body: { reason: string }): Promise<unknown> {
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(id)}/finance-reject`, { method: 'POST', body });
}

// ---- finance portfolio + off-cycle settlement -------------------------------

export function listClaims(status?: ExpenseClaimStatus[]): Promise<{ claims: ExpenseClaim[] }> {
  const qs = status && status.length > 0 ? `?status=${status.join(',')}` : '';
  return apiRequest(`/v1/expense/claims${qs}`);
}

export function markPaidOffcycle(id: string, body: { method: string; reference: string }): Promise<ExpenseClaim> {
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(id)}/mark-paid`, { method: 'POST', body });
}

// ---- receipts ---------------------------------------------------------------

export function listReceipts(claimId: string): Promise<{ receipts: ExpenseReceipt[] }> {
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(claimId)}/receipts`);
}

export function uploadReceipt(claimId: string, file: File): Promise<ExpenseReceipt> {
  const form = new FormData();
  form.append('file', file);
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(claimId)}/receipt`, { method: 'POST', body: form });
}

export function downloadReceipt(claimId: string, artifactId: string, filename: string): Promise<void> {
  return apiDownload(
    `/v1/expense/claims/${encodeURIComponent(claimId)}/receipts/${encodeURIComponent(artifactId)}/download`,
    filename,
  );
}

export function deleteReceipt(claimId: string, artifactId: string): Promise<unknown> {
  return apiRequest(`/v1/expense/claims/${encodeURIComponent(claimId)}/receipts/${encodeURIComponent(artifactId)}`, {
    method: 'DELETE',
  });
}
